import { css } from "@emotion/react"
import { groupBy, difference } from "lodash"
import { useCallback, useMemo } from "react"
import { colors } from "shared-ui"
import TestCategoryCheckbox from "../testCategory/TestCategoryCheckbox"

export default function MultipleIndexCategorySelectionGrid({
    categories,
    selectedCatIds,
    setSelectedCatIds,
    maxSelected = 100,
    indexCategoryGroups = ['performance', 'boot'],
    ...props
}) {

    const groupedCategories = useMemo(() => {
        const grouped = groupBy(categories, 'sport')
        for (const [key, array] of Object.entries(grouped)) {
            grouped[key] = array.toSorted((a, b) => {
                // Compare by sortRank
                if (a.sortRank < b.sortRank) return -1
                if (a.sortRank > b.sortRank) return 1
                // If weights are the same, compare ids
                if (a.id < b.id) return -1
                if (a.id > b.id) return 1
                return -1
            })
        }
        return grouped
    }, [categories])

    const customSortedGroupedCategoriesEntries = useMemo(() => {
        return Object.entries(groupedCategories)
            .toSorted((a, b) => a[0].localeCompare(b[0]))
    }, [groupedCategories])

    const handleToggleCategory = useCallback((id) => {
        if (selectedCatIds.includes(id)) {
            setSelectedCatIds(current => current.filter(entry => entry !== id))
        } else if (selectedCatIds.length < maxSelected) {
            setSelectedCatIds(current => [...current, id])
        }
    }, [selectedCatIds, maxSelected])

    const handleToggleGroup = useCallback((groupCategories) => {

        const groupIds = groupCategories.map(cat => cat.id)
        const allGroupIdsChecked = difference(groupIds, selectedCatIds).length === 0

        // full or at capacity: clear whole group
        setSelectedCatIds(curr => curr.filter(id => !groupIds.includes(id)))
        if (allGroupIdsChecked) return
        const freeSpots = maxSelected - selectedCatIds.length
        if (freeSpots === 0) return

        // add up to capacity
        setSelectedCatIds(curr => {
            const toAdd = groupIds.slice(0, maxSelected - curr.length)
            return [...curr, ...toAdd]
        })

    }, [selectedCatIds, maxSelected])

    return <div css={style.group}>
        {customSortedGroupedCategoriesEntries.map(([groupName, groupCategories]) => {

            const cats = groupCategories.filter(c => indexCategoryGroups.includes(c.indexCategoryGroup))

            if (cats.length === 0) return <div css={css`display: none;`} />

            return <div key={groupName} style={{ display: 'flex', flexDirection: 'column' }}>
                <button
                    onClick={() => { handleToggleGroup(cats) }}
                    css={style.groupButton}
                >
                    <h3 style={{
                        color: colors.player,
                        fontWeight: 'bold',
                        fontSize: '20px',
                        whiteSpace: 'nowrap'
                    }} >
                        {groupName.toUpperCase()}
                    </h3>
                </button>
                <div css={style.wrapper}>
                    {cats.map((cat) => (
                        <TestCategoryCheckbox
                            key={cat.id}
                            style={{ fontSize: '0.5em' }}
                            testCategory={{
                                ...cat,
                                icon: 'gauge',
                                group: 'player',
                            }}
                            checked={selectedCatIds.includes(cat.id)}
                            onClick={() => { handleToggleCategory(cat.id) }}
                        />
                    ))}
                </div>
            </div>

        })}
    </div>
}

const style = {
    group: css`
        display: flex;
        flex-direction: column;
    `,
    wrapper: css`
        display: inline-flex;
        flex-wrap: wrap;
        margin: 0 0 1em 0;
    `,
    groupButton: css`
        width: min-content;
        background: ${colors.eventDark};
        cursor: pointer;
        border: none;
        border-radius: 2px;
        padding: 0.6em;
        margin-left: 0.2em;
        border: 2px solid ${colors.eventLight};

        &:hover {
            background: ${colors.eventLight};
        }
    `
}